import { SkyLink, SkyText } from "@skypointcloud/skypoint-ui";
import type { ReactNode } from "react";
import { useLocation } from "wouter";
import { helpHrefFor } from "@/lib/helpLinks";
import { useTopbarSlot } from "./TopbarSlotContext";

/**
 * The one page header in the app (UX-1, GUIDELINES.md §3.4).
 *
 * Rendered once from App.tsx for every route, never by a page. Pages contribute
 * their actions through `TopbarActionsPortal`, which lands them in the slot on
 * the right of this row. One header means the title row and its divider sit at
 * the same place on every screen, which a per-page header never manages for
 * long. The breadcrumb row above it (`ShellBreadcrumb`) is the one that meets
 * the nav drawer's header row.
 */
export function PageHeader({
  title,
  description,
  noHelp = false,
}: {
  title: string;
  description?: ReactNode;
  /** #1812: an address of a feature that does not exist while off links to no guide page. */
  noHelp?: boolean;
}) {
  const { setSlot } = useTopbarSlot();
  // Round 7: this screen's page of the user guide (`lib/helpLinks.ts`), served at /help.
  const [location] = useLocation();
  const help = noHelp ? null : helpHrefFor(location);

  return (
    <header className="relative z-30 shrink-0 bg-background">
      <div className="flex min-h-11 items-center px-4 py-3">
        {/* The row wraps: when the title and the actions do not fit side by side (a phone),
            the actions move under the title and wrap themselves, instead of a row that could
            not shrink squeezing the title to a word a line and overflowing the shell. No
            `sm:` variant here: skypoint-ui's stylesheet re-ships base utilities after the
            app's, so a responsive override of `flex-col` or `flex-wrap` loses the cascade. */}
        <div className="flex w-full flex-wrap items-center gap-x-8 gap-y-2">
          <div className="min-w-0">
            {/* The type ramp's page title (rules.md §1: H1 28 to 32 px) with a body-size
                description, as the template's `.page-header`. */}
            <SkyText as="h1" size={700} weight="semibold" block truncate>
              {title}
            </SkyText>
            {description ? (
              <SkyText as="p" size={300} color="muted" block>
                {description}
              </SkyText>
            ) : null}
          </div>
          <div className="ml-auto flex max-w-full shrink-0 items-center gap-2">
            {help ? (
              <SkyLink
                href={help}
                target="_blank"
                appearance="subtle"
                size="small"
                testId="page-help-link"
                aria-label="Help for this page, opens the user guide in a new tab"
              >
                Help for this page
              </SkyLink>
            ) : null}
            {/* Portal target for TopbarActionsPortal. */}
            <div
              id="topbar-actions-slot"
              ref={setSlot}
              className="flex flex-wrap items-center gap-1"
            />
          </div>
        </div>
      </div>
      <div className="border-t border-border" />
    </header>
  );
}
